import { ChatAlt2Icon } from "@heroicons/react/outline";
import { startCase } from "lodash";
import { Clock, Workflow } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { toast } from "sonner";
import { flowStateService, useCustomerFlowStates } from "@/api/flow-state";
import { Customer, IntegrationType, List, ListMember } from "@/api/types";
import type { FlowStateWithFlow } from "@/api/types/flow-state";
import { ApiError, handleApiError } from "@/api/utils";
import { badgeVariants } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SystemBadge } from "@/modules/integration/system-integration-icon";
import { formatFlowName, getNextActionDescription } from "@/lib/flow-utils";
import {
  cn,
  formatCurrency,
  formatDate,
  formatDateRelative,
  formatPhoneNumber,
  getCustomerName,
  getCustomerSource,
  removeSystemPrefixFromString,
} from "@/lib/utils";
import { CustomerMessageButton } from "@/ui/components/customer-message-link";
import { TableLoading } from "@/ui/elements";
import useActiveGym from "@/ui/hooks/active-gym";
import useMatchMutate from "@/ui/hooks/match-mutate";
import { AddToListModal } from "./add-to-list-modal";
import { CustomerAvatar } from "./customer-avatar";
import { useCustomerDetails } from "./hooks/mariana-tek";
import { OptedOutBadge } from "./opted-out-badge";
import { IntegrationImage } from "./source-badge";
export default function Profile(props: {
  customer: Customer;
  listMembers?: ListMember[];
  addToListOpen: boolean;
  setAddToListOpen: (open: boolean) => void;
  handleAddToList: () => void;
}) {
  const customerSource = getCustomerSource(props.customer);
  const { data: marianaTekCustomerDetails } = useCustomerDetails(props.customer);
  const listMembers = props.listMembers?.filter((item) => !!item.list) ?? [];
  const { gym } = useActiveGym();
  const matchMutate = useMatchMutate();
  const [flowStateToRemove, setFlowStateToRemove] = useState<FlowStateWithFlow | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const { data: flowStates } = useCustomerFlowStates(
    gym?.id && props.customer.id
      ? {
          gym: gym.id,
          customer: props.customer.id,
          status: "queued,waiting",
        }
      : undefined,
  );

  const handleRemoveFromFlow = async () => {
    if (!flowStateToRemove) return;

    setIsRemoving(true);
    try {
      await flowStateService.readAndDelete(flowStateToRemove.id);
      toast.success(`Removed from ${formatFlowName(flowStateToRemove.flow.name)}`);
      await matchMutate(/\/flow-states/);
      setFlowStateToRemove(null);
    } catch (err) {
      if (err instanceof ApiError) {
        handleApiError(err);
      }
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <div className="py-6">
      <div className="md:px-6 flex flex-col items-center gap-3 px-4">
        <CustomerAvatar customer={props.customer} className="w-16 h-16" />
        <div className="text-center">
          <h1 className="text-xl font-semibold">{getCustomerName(props.customer)}</h1>
          <p className="text-muted-foreground text-xs">
            {customerSource !== "helios"
              ? `Added from ${startCase(customerSource)} on `
              : "Created on "}
            <time dateTime={props.customer.createdAt}>
              {formatDate(props.customer.createdAt, "DDD")}
            </time>
          </p>
        </div>
      </div>
      <div className="md:px-6 flex justify-around px-4 py-4 border-b">
        <AddToListModal
          open={props.addToListOpen}
          onOpenChange={props.setAddToListOpen}
          onSuccess={props.handleAddToList}
          customer={props.customer}
          listMemberships={listMembers}
          className="w-1/2 mr-2"
          variant="outline"
          size="sm"
        />
        <CustomerMessageButton customer={props.customer} className="w-1/2 ml-2" size="sm">
          <ChatAlt2Icon className="w-4 h-4 mr-2" aria-hidden="true" />
          Send message
        </CustomerMessageButton>
      </div>
      {customerSource === "mariana-tek" && (
        <div className="md:px-6 px-4 py-4 space-y-5 border-b">
          <div className="flex items-center">
            <span className="block shrink-0 w-6 h-6 bg-white border rounded-[6px] p-0.5 mr-2">
              <IntegrationImage integrationType={IntegrationType.marianaTek} />
            </span>
            <h2 className="font-medium">Mariana Tek details</h2>
          </div>
          {marianaTekCustomerDetails ? (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="flex items-center justify-between">
                    <span className="text-muted-foreground block text-xs">Completed classes</span>
                  </p>
                  <p className="text-sm font-medium mt-0.5">
                    {marianaTekCustomerDetails?.attributes.completed_class_count || 0}
                  </p>
                </div>
                {marianaTekCustomerDetails?.attributes.last_class_taken_date && (
                  <div>
                    <p className="flex items-center justify-between">
                      <span className="text-muted-foreground block text-xs">Last class date</span>
                    </p>
                    <p className="text-sm font-medium mt-0.5">
                      {formatDateRelative(
                        marianaTekCustomerDetails?.attributes.last_class_taken_date,
                      )}
                    </p>
                  </div>
                )}
              </div>
              <div>
                <p className="flex items-center justify-between">
                  <span className="text-muted-foreground block text-xs">Top location</span>
                </p>
                <p className="text-sm font-medium mt-0.5">
                  {marianaTekCustomerDetails?.attributes.top_location_name || "No top location"}
                </p>
              </div>
            </>
          ) : (
            <TableLoading />
          )}
        </div>
      )}
      {customerSource === "tablelist" && (
        <div className="md:px-6 px-4 py-4 space-y-5 border-b">
          <div className="flex items-center">
            <span className="block shrink-0 w-6 h-6 bg-white border rounded-[6px] p-0.5 mr-2">
              <IntegrationImage integrationType={IntegrationType.tablelist} />
            </span>
            <h2 className="font-medium">Tablelist details</h2>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="flex items-center justify-between">
                <span className="text-muted-foreground block text-xs">Total visits</span>
              </p>
              <p className="text-sm font-medium mt-0.5">
                {props.customer.properties.tablelist.stats?.totalVisits || 0}
              </p>
            </div>
            <div>
              <p className="flex items-center justify-between">
                <span className="text-muted-foreground block text-xs">Lifetime spend</span>
              </p>
              <p className="text-sm font-medium mt-0.5">
                {formatCurrency(props.customer.properties.tablelist.stats?.totalSpend || 0)}
              </p>
            </div>
            <div>
              <p className="flex items-center justify-between">
                <span className="text-muted-foreground block text-xs">Average spend</span>
              </p>
              <p className="text-sm font-medium mt-0.5">
                {formatCurrency(props.customer.properties.tablelist.stats?.averageSpend || 0)}
              </p>
            </div>
            {props.customer.properties.tablelist.stats?.latestVisit && (
              <div>
                <p className="flex items-center justify-between">
                  <span className="text-muted-foreground block text-xs">Latest visit</span>
                </p>
                <p className="text-sm font-medium mt-0.5">
                  {formatDateRelative(props.customer.properties.tablelist.stats?.latestVisit)}
                </p>
              </div>
            )}
          </div>
          <div>
            <p className="flex items-center justify-between">
              <span className="text-muted-foreground block text-xs">Notes</span>
            </p>
            <p className="text-sm font-medium mt-0.5">
              {props.customer.properties.tablelist.notes || "No notes"}
            </p>
          </div>
          <div>
            <p className="flex items-center justify-between mb-1.5">
              <span className="text-muted-foreground block text-xs">Tags</span>
            </p>
            <div className="flex flex-wrap gap-2">
              {props.customer.properties.tablelist.tags?.length
                ? props.customer.properties.tablelist.tags.map((tag: string) => (
                    <div
                      key={tag}
                      className={cn(badgeVariants({ variant: "outline" }), "py-1 px-2")}
                    >
                      <span className="text-xs font-medium leading-none">{tag}</span>
                    </div>
                  ))
                : "No tags"}
            </div>
          </div>
        </div>
      )}
      {/* Customer Info */}
      <div className="md:px-6 px-4 py-4 space-y-5 border-b">
        <div>
          <p className="flex items-center justify-between">
            <span className="text-muted-foreground block text-xs">Phone Number</span>
            <OptedOutBadge customer={props.customer} />
          </p>
          <p className="text-sm font-medium mt-0.5">
            {formatPhoneNumber(props.customer.phoneNumber)}
          </p>
        </div>
        <div>
          <p className="flex items-center justify-between">
            <span className="text-muted-foreground block text-xs">Email</span>
          </p>
          <p className="text-sm font-medium mt-0.5">{props.customer.email || "No email"}</p>
        </div>
      </div>

      {/* Active Flows */}
      {flowStates && flowStates.length > 0 && (
        <div className="md:px-6 px-4 py-4 border-b">
          <p className="flex items-center gap-2 mb-3">
            <Workflow className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground text-xs">Active Flows</span>
          </p>
          <div className="space-y-3">
            {flowStates.map((flowState) => (
              <div key={flowState.id} className="flex flex-col gap-1">
                <div className="flex items-center justify-between gap-2">
                  <Link
                    href={`/flows/${flowState.flow.id}`}
                    className="text-sm font-medium hover:underline truncate min-w-0"
                  >
                    {formatFlowName(flowState.flow.name)}
                  </Link>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 flex-shrink-0 text-xs text-muted-foreground hover:text-destructive"
                    onClick={() => setFlowStateToRemove(flowState)}
                  >
                    Remove
                  </Button>
                </div>
                {flowState.waitUntil && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {getNextActionDescription(flowState) || "Next action"}{" "}
                    {formatDateRelative(flowState.waitUntil, { specificity: "luxon" })}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lists & Segments */}
      <div className="md:px-6 px-4 py-4">
        <p className="flex items-center justify-between mb-3">
          <span className="text-muted-foreground text-xs">Lists &amp; Segments</span>
        </p>
        <div className="flex flex-wrap gap-2">
          {listMembers.length
            ? listMembers.map((listMember) => (
                <Link
                  key={listMember.id}
                  className={cn(
                    badgeVariants({ variant: "outline" }),
                    "py-1.5 px-4 hover:underline",
                  )}
                  href={`/lists/${(listMember.list as List).id}`}
                >
                  <span className="text-sm font-medium leading-none inline-flex items-center gap-2">
                    {(listMember.list as List).system && <SystemBadge />}
                    {removeSystemPrefixFromString((listMember.list as List).name)}
                  </span>
                </Link>
              ))
            : null}
          <AddToListModal
            open={props.addToListOpen}
            onOpenChange={props.setAddToListOpen}
            onSuccess={props.handleAddToList}
            customer={props.customer}
            listMemberships={props.listMembers}
            className={cn(
              badgeVariants({ variant: "outline" }),
              "py-1.5 px-4 hover:underline border-dashed",
            )}
            variant="outline"
          />
        </div>
      </div>

      {/* Remove from flow confirmation dialog */}
      <Dialog
        open={!!flowStateToRemove}
        onOpenChange={(open) => !open && setFlowStateToRemove(null)}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Remove from flow</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this customer from{" "}
              <span className="font-medium text-foreground">
                {flowStateToRemove?.flow.name ? formatFlowName(flowStateToRemove.flow.name) : ""}
              </span>
              ? They will no longer receive messages from this flow.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setFlowStateToRemove(null)}
              disabled={isRemoving}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRemoveFromFlow} disabled={isRemoving}>
              {isRemoving ? "Removing..." : "Remove"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
